import { Plus, Trash, ArrowUp, ArrowDown } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { SimpleDropdown } from '@/components/ui/simple-dropdown';
import { Badge } from '@/components/ui/badge';
import type { QuestionItem, AnswerOptionItem } from 'screens/settings/DueDiligenceCategoryEditor/types';
import { createEmptyAnswerOption, STATUS_COLOR_OPTIONS } from 'screens/settings/DueDiligenceCategoryEditor/types';
import { DueDiligenceStatusColor, type ConditionGroup } from 'shared/types/due-diligence';
import { ConditionBuilderV2 } from 'components/dueDiligence/ConditionBuilder';

interface WizardStep3Props {
  question: QuestionItem;
  onUpdate: (updates: Partial<QuestionItem>) => void;
  existingQuestions?: QuestionItem[];
}

function getStatusColorDot(color: DueDiligenceStatusColor) {
  const colorMap = {
    [DueDiligenceStatusColor.GREEN]: 'bg-green-500',
    [DueDiligenceStatusColor.ORANGE]: 'bg-orange-500',
    [DueDiligenceStatusColor.RED]: 'bg-red-500',
    [DueDiligenceStatusColor.TRANSPARENT]: 'bg-gray-300',
  };
  return colorMap[color] || 'bg-gray-300';
}

export function WizardStep3AnswerOptions({ question, onUpdate, existingQuestions }: WizardStep3Props) {
  const handleAddOption = () => {
    try {
      const isFirst = question.answerOptions.length === 0;
      const newOption = createEmptyAnswerOption(isFirst);
      const updatedOptions = [...question.answerOptions, newOption];
      onUpdate({ answerOptions: updatedOptions });
    } catch (error) {
      console.error('Error adding answer option:', error);
      throw error;
    }
  };

  const handleRemoveOption = (index: number) => {
    try {
      const updatedOptions = question.answerOptions.filter((_, i) => i !== index);
      onUpdate({ answerOptions: updatedOptions });
    } catch (error) {
      console.error('Error removing answer option:', error);
      throw error;
    }
  };

  const handleMoveOption = (index: number, direction: 'up' | 'down') => {
    try {
      const newIndex = direction === 'up' ? index - 1 : index + 1;
      if (newIndex < 0 || newIndex >= question.answerOptions.length) return;

      const updatedOptions = [...question.answerOptions];
      [updatedOptions[index], updatedOptions[newIndex]] = [updatedOptions[newIndex], updatedOptions[index]];
      onUpdate({ answerOptions: updatedOptions });
    } catch (error) {
      console.error('Error moving answer option:', error);
      throw error;
    }
  };

  const handleUpdateOption = (index: number, updates: Partial<AnswerOptionItem>) => {
    try {
      const updatedOptions = [...question.answerOptions];
      updatedOptions[index] = { ...updatedOptions[index], ...updates };
      onUpdate({ answerOptions: updatedOptions });
    } catch (error) {
      console.error('Error updating answer option:', error);
      throw error;
    }
  };

  const handleConditionChange = (index: number, conditionRoot: ConditionGroup | null) => {
    try {
      handleUpdateOption(index, { conditionRoot });
    } catch (error) {
      console.error('Error updating condition:', error);
      throw error;
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-foreground">Antwoordopties</h3>
        <p className="text-sm text-muted-foreground mt-1">
          Voeg antwoordopties toe en configureer voorwaarden.
        </p>
      </div>

      <div className="space-y-4">
        {question.answerOptions.map((option, index) => (
          <Card key={option.id} className="p-4">
            <div className="space-y-4">
              {/* Header with reorder buttons */}
              <div className="flex items-center gap-2">
                <div className="flex gap-1">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => handleMoveOption(index, 'up')}
                    disabled={index === 0}
                  >
                    <ArrowUp weight="regular" className="w-4 h-4" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => handleMoveOption(index, 'down')}
                    disabled={index === question.answerOptions.length - 1}
                  >
                    <ArrowDown weight="regular" className="w-4 h-4" />
                  </Button>
                </div>

                <Badge variant="outline" className="text-xs">
                  Optie {index + 1}
                </Badge>

                <div className="flex-1" />

                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => handleRemoveOption(index)}
                  disabled={question.answerOptions.length === 1}
                >
                  <Trash weight="regular" className="w-4 h-4 text-destructive" />
                </Button>
              </div>

              {/* Option fields */}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <label className="text-xs font-medium text-muted-foreground">Label</label>
                  <Input
                    value={option.label}
                    onChange={(e) => handleUpdateOption(index, { label: e.target.value })}
                    placeholder="bijv. In orde"
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-xs font-medium text-muted-foreground">Waarde</label>
                  <Input
                    value={option.value}
                    onChange={(e) => handleUpdateOption(index, { value: e.target.value })}
                    placeholder="bijv. ok"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <label className="text-xs font-medium text-muted-foreground">Status kleur</label>
                  <div className="flex items-center gap-2">
                    <div className={`w-3 h-3 rounded-full ${getStatusColorDot(option.statusColor)}`} />
                    <SimpleDropdown
                      options={STATUS_COLOR_OPTIONS.map(opt => ({ value: opt.value, label: opt.label }))}
                      value={option.statusColor}
                      onChange={(value) => handleUpdateOption(index, { statusColor: value as DueDiligenceStatusColor })}
                      placeholder="Kies kleur"
                    />
                  </div>
                </div>

                <div className="flex items-end">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={option.isDefault}
                      onChange={(e) => handleUpdateOption(index, { isDefault: e.target.checked })}
                      className="w-4 h-4 rounded border-border"
                    />
                    <span className="text-xs font-medium text-muted-foreground">Standaard optie</span>
                  </label>
                </div>
              </div>

              {/* Condition builder */}
              <div className="space-y-2 pt-2 border-t">
                <label className="text-xs font-medium text-muted-foreground">Voorwaarden</label>
                <ConditionBuilderV2
                  conditionRoot={option.conditionRoot || null}
                  onChange={(newCondition) => handleConditionChange(index, newCondition)}
                  availableQuestions={existingQuestions?.map(q => ({
                    id: q.id || 0,
                    displayName: q.displayName,
                    fieldName: q.fieldName,
                    answerOptions: q.answerOptions.map(ao => ({
                      id: ao.id,
                      label: ao.label,
                      value: ao.value,
                      statusColor: ao.statusColor,
                      active: ao.active,
                    })),
                  }))}
                />
              </div>
            </div>
          </Card>
        ))}

        <Button type="button" variant="outline" onClick={handleAddOption} className="w-full">
          <Plus weight="regular" className="w-4 h-4 mr-2" />
          Optie toevoegen
        </Button>
      </div>
    </div>
  );
}
